import { useState, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import { movies, categories } from "@/mocks/movies";

function MovieCard({ movie }: { movie: typeof movies[0] }) {
  return (
    <Link
      to={`/detail/${movie.id}`}
      className="group block rounded-lg overflow-hidden bg-background-50 border border-background-200/60 hover:border-primary-200 transition-all duration-300"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={movie.poster}
          alt={movie.title}
          title={`${movie.title} - 天美麻花星空影视免费在线观看`}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute top-2 right-2 bg-primary-500 text-white text-xs font-bold px-2 py-1 rounded-md">
          {movie.rating}
        </div>
        {movie.status && (
          <div className="absolute bottom-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs px-2 py-1 rounded-md">
            {movie.status}
          </div>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-heading font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-500 transition-colors">
          {movie.title}
        </h3>
        <div className="flex items-center gap-2 mt-1.5 flex-wrap">
          <span className="text-xs text-foreground-500">{movie.year}</span>
          <span className="text-xs text-foreground-400">|</span>
          <span className="text-xs text-foreground-500">{movie.categoryName}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const categoryInfo = categories.find((c) => c.slug === slug) || categories[0];
  const [activeSort, setActiveSort] = useState<"rating" | "year">("rating");

  const filteredMovies = useMemo(() => {
    let list = movies.filter((m) => m.category === slug);
    if (activeSort === "rating") {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else {
      list = [...list].sort((a, b) => b.year - a.year);
    }
    return list;
  }, [slug, activeSort]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <div className="pt-16">
        <Breadcrumb
          items={[
            { label: "首页", href: "/" },
            { label: categoryInfo.name },
          ]}
        />
      </div>

      <main className="w-full px-4 md:px-8 py-6 md:py-10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              全部{categoryInfo.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-1">
              共 {filteredMovies.length} 部{categoryInfo.name}资源
            </p>
          </div>
          <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
            <button
              onClick={() => setActiveSort("rating")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                activeSort === "rating"
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              按评分
            </button>
            <button
              onClick={() => setActiveSort("year")}
              className={`px-4 py-1.5 text-sm font-medium rounded-full transition-all whitespace-nowrap ${
                activeSort === "year"
                  ? "bg-primary-500 text-white"
                  : "text-foreground-600 hover:text-foreground-900"
              }`}
            >
              按年份
            </button>
          </div>
        </div>

        {/* Category Tabs */}
        <div className="flex flex-wrap gap-2 mb-8">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={`/category/${cat.slug}`}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                cat.slug === slug
                  ? "bg-primary-500 text-white"
                  : "bg-background-100 text-foreground-600 hover:bg-background-200"
              }`}
            >
              <i className={`${cat.icon} text-sm`} />
              {cat.name}
            </Link>
          ))}
        </div>

        {filteredMovies.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
            {filteredMovies.map((movie) => (
              <MovieCard key={movie.id} movie={movie} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-emotion-sad-line text-3xl text-foreground-400" />
            </div>
            <p className="text-foreground-500">暂无{categoryInfo.name}资源</p>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
}